import { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { UserCircle2, Building2, ShieldCheck, KeyRound, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MATERIALS = ["PET", "HDPE", "HDPE(N)", "PP", "PP(N)", "PE(N)", "PE(M)", "UBC", "Mixed Plastics"];

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const { data } = await api.get("/auth/profile");
      setProfile(data);
      setForm({
        name: data.name || "",
        phone: data.phone || "",
        // Personal contact (shown for non-facility roles; for facility the
        // address field below is the org-level facility address)
        address: data.facility ? (data.facility.address || "") : (data.address || ""),
        company_name: data.company_name || "",
        facility_name: data.facility?.name || "",
        registration_number: data.facility?.registration_number || "",
        materials_processed: data.facility?.materials_processed || [],
        employees_count: data.facility?.employees_count || 1,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  function toggleMaterial(m) {
    setForm((f) => {
      const has = f.materials_processed.includes(m);
      return {
        ...f,
        materials_processed: has
          ? f.materials_processed.filter((x) => x !== m)
          : [...f.materials_processed, m],
      };
    });
  }

  async function save() {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim(),
      };
      if (profile.role === "facility") {
        body.facility_name = form.facility_name.trim();
        body.address = form.address.trim();
        body.registration_number = form.registration_number.trim();
        body.materials_processed = form.materials_processed;
        body.employees_count = Number(form.employees_count);
      } else {
        // Personal contact details for non-facility roles
        body.address = form.address.trim();
        body.company_name = form.company_name.trim();
      }
      const { data } = await api.patch("/auth/profile", body);
      setProfile(data);
      toast.success("Profile updated");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function changePassword() {
    if (!pwd.current || !pwd.next) {
      toast.error("Current and new password are required");
      return;
    }
    if (pwd.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setPwdBusy(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      setPwd({ current: "", next: "", confirm: "" });
      if (data?.pending_password_change) {
        // Admin role: server emailed a 15-min confirmation link instead of
        // applying the change. Tell the user clearly.
        toast.success(
          "Check your email — we sent you a link to confirm this password change. The link expires in 15 minutes.",
          { duration: 7000 }
        );
      } else {
        toast.success("Password updated");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPwdBusy(false);
    }
  }

  if (!profile || !form) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Topbar subtitle={user?.role || ""} />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 text-sm text-[#3F3F3F]">
          Loading profile…
        </div>
      </div>
    );
  }

  const statusTint =
    profile.status === "Active"
      ? { bg: "#D2E4D6", fg: "#056839" }
      : profile.status === "Rejected" || profile.status === "Suspended"
      ? { bg: "#A0522D22", fg: "#A0522D" }
      : { bg: "#E4D5B7", fg: "#A0522D" };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="profile-page">
      <Topbar subtitle={profile.role} />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">My account</div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#000000] mt-1">
              Profile
            </h1>
            <p className="text-sm text-[#3F3F3F] mt-1">
              Review and update the information on your account.
            </p>
          </div>
          <span
            data-testid="profile-status-badge"
            className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-semibold"
            style={{ background: statusTint.bg, color: statusTint.fg }}
          >
            <ShieldCheck className="inline w-3 h-3 mr-1 -mt-0.5" />
            {profile.status}
          </span>
        </div>

        {/* Personal info */}
        <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="profile-personal-card">
          <CardContent className="p-5 md:p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#D2E4D6]">
                <UserCircle2 className="w-5 h-5 text-[#056839]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Personal</div>
                <h3 className="font-heading text-lg font-semibold text-[#000000]">
                  Your details
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                  Full name <span className="text-[#A0522D] normal-case">*</span>
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="profile-name"
                  className="h-11 mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                  Phone
                </Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="profile-phone"
                  className="h-11 mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                  Email (not editable)
                </Label>
                <Input
                  value={profile.email}
                  disabled
                  data-testid="profile-email"
                  className="h-11 mt-1 bg-[#F9F8F6] text-[#3F3F3F]/70 cursor-not-allowed"
                />
                <p className="text-[11px] text-[#3F3F3F]/60 mt-1">
                  Email cannot be changed. Contact an administrator if you need to update it.
                </p>
              </div>
              {profile.role !== "facility" && (
                <>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                      Company / Organisation
                    </Label>
                    <Input
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      data-testid="profile-company-name"
                      placeholder="e.g. PROcess Operations"
                      className="h-11 mt-1"
                    />
                  </div>
                  <div className="md:col-span-1" />
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                      Address
                    </Label>
                    <Input
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      data-testid="profile-address"
                      placeholder="Street, city, postcode"
                      className="h-11 mt-1"
                    />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Facility-only org info */}
        {profile.role === "facility" && (
          <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="profile-facility-card">
            <CardContent className="p-5 md:p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#E4D5B7]">
                  <Building2 className="w-5 h-5 text-[#A0522D]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Organisation</div>
                  <h3 className="font-heading text-lg font-semibold text-[#000000]">
                    Facility details
                  </h3>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                    Facility name <span className="text-[#A0522D] normal-case">*</span>
                  </Label>
                  <Input
                    value={form.facility_name}
                    onChange={(e) => setForm({ ...form, facility_name: e.target.value })}
                    data-testid="profile-facility-name"
                    className="h-11 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                    Business registration #
                  </Label>
                  <Input
                    value={form.registration_number}
                    onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
                    data-testid="profile-reg-number"
                    className="h-11 mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                    Address
                  </Label>
                  <textarea
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    data-testid="profile-address"
                    rows={2}
                    className="mt-1 w-full rounded-lg border-2 border-[#E4D5B7] focus:border-[#056839] focus:outline-none focus:ring-2 focus:ring-[#056839]/15 px-3 py-2 text-sm bg-white"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                    Employees
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.employees_count}
                    onChange={(e) => setForm({ ...form, employees_count: e.target.value })}
                    data-testid="profile-employees"
                    className="h-11 mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                    Materials processed <span className="text-[#A0522D] normal-case">*</span>
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="profile-materials">
                    {MATERIALS.map((m) => {
                      const checked = form.materials_processed.includes(m);
                      return (
                        <label
                          key={m}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 cursor-pointer text-sm transition-colors ${
                            checked
                              ? "border-[#056839] bg-[#D2E4D6] text-[#056839] font-medium"
                              : "border-[#E4D5B7] bg-white text-[#3F3F3F]/70 hover:border-[#69A488]"
                          }`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleMaterial(m)}
                            data-testid={`profile-material-${m}`}
                          />
                          {m}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={busy}
            data-testid="profile-save-btn"
            className="h-11 gfs-bg-primary hover:bg-[#044E2A] text-white px-6"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </div>

        {/* Password change */}
        <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="profile-password-card">
          <CardContent className="p-5 md:p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#A0522D]/15">
                <KeyRound className="w-5 h-5 text-[#A0522D]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Security</div>
                <h3 className="font-heading text-lg font-semibold text-[#000000]">
                  Change password
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                  Current password
                </Label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={pwd.current}
                  onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                  data-testid="profile-current-password"
                  className="h-11 mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                  New password
                </Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={pwd.next}
                  onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                  data-testid="profile-new-password"
                  className="h-11 mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                  Confirm new
                </Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={pwd.confirm}
                  onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                  data-testid="profile-confirm-password"
                  className="h-11 mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={changePassword}
                disabled={pwdBusy}
                data-testid="profile-password-save-btn"
                variant="outline"
                className="h-11 border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white"
              >
                {pwdBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Update password
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
